import { useQuery, UseQueryResult } from 'react-query';
import axiosInstance from 'utils/axios';
import { PortalListingType } from 'containers/user/Portal/PortalListingType';
import { FormSubmissionListingType } from 'containers/dashboard/FormSubmission/FormSubmissionListingType';
import {
  GATEWAY_DETAIL_API_URL,
  GATEWAY_LISTING_API_URL,
  FORMS_LISTING_API_URL,
  PORTAL_LISTING_API_URL,
  TRANSACTION_LISTING_API_URL,
  GET_PORTAL_FORM_API_URL,
  FORM_SUBMISSION_LISTING_API_URL,
  GET_ALL_HUBSPOT_FORM_API_URL,
  BILLING_DETAIL_API_URL,
  RAZORPAY_PLANS_URL,
  HUBSPOT_SEARCH_API_URL,
  CREATE_PRODUCT_URL,
  EXISTING_HUBSPOT_PRODUCT_URL,
  FETCH_HUBSPOT_PRODUCT,
  SUBSCRIPTION_LISTING_API_URL,
} from './apiConstants';
import {
  GatewayDetailType,
  GatewayListingType,
} from 'containers/manage/gateways/GatewaysListingType';
import { TransactionListingType } from 'containers/dashboard/Transaction/TransactionListingType';
import {
  FormDetailType,
  FormListingType,
  HubspotFormsType,
} from 'containers/dashboard/Forms/FormListingType';
import { BillingDetailType } from 'containers/payments/PaymentType';
import { RazorpayPlanResponse } from 'containers/forms/child/FormSettingsTypes';
import { HubspotProductList } from 'containers/products/HubspotProductList';
import { HOST_API } from 'config';
import { HubspotProduct } from 'types/product';
import { RootObjectProductListingType } from 'containers/products/ProductListingType';

const portalListingRequest = async (): Promise<PortalListingType> => {
  const { data } = await axiosInstance.get<PortalListingType>(
    `${PORTAL_LISTING_API_URL}`
  );

  return data;
};

export const usePortalListing = (): UseQueryResult<PortalListingType> => {
  return useQuery([PORTAL_LISTING_API_URL], () => portalListingRequest(), {
    refetchOnWindowFocus: false,
    cacheTime: 10,
    staleTime: 0,
  });
};

// Gateway - Section //

const gatewayListingRequest = async (): Promise<GatewayListingType> => {
  const { data } = await axiosInstance.get<GatewayListingType>(
    `${GATEWAY_LISTING_API_URL}`
  );

  return data;
};

export const useGatewayListing = (props?: {
  [key: string]: any;
}): UseQueryResult<GatewayListingType> => {
  return useQuery([GATEWAY_LISTING_API_URL], () => gatewayListingRequest(), {
    refetchOnWindowFocus: false,
    cacheTime: 5000,
    ...(props || {}),
  });
};

const gatewayDetailRequest = async (
  gatewayId: string
): Promise<GatewayDetailType> => {
  const { data } = await axiosInstance.get<GatewayDetailType>(
    `${GATEWAY_DETAIL_API_URL}/${gatewayId}`
  );

  return data;
};

export const useGatewayDetail = (
  gatewayId: string
): UseQueryResult<GatewayDetailType> => {
  return useQuery(
    [GATEWAY_DETAIL_API_URL, gatewayId],
    () => gatewayDetailRequest(gatewayId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

// Forms - Section //

const formsListingRequest = async (
  portalId: number
): Promise<FormListingType> => {
  const { data } = await axiosInstance.get<FormListingType>(
    `${FORMS_LISTING_API_URL}?portal_id=${portalId}`
  );

  return data;
};

export const useFormListing = (
  portalId: number
): UseQueryResult<FormListingType> => {
  return useQuery(
    [FORMS_LISTING_API_URL],
    () => formsListingRequest(portalId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const formsListingTable = async (
  portalId: number,
  skip: number | null,
  limit: number | null
): Promise<FormListingType> => {
  const { data } = await axiosInstance.post<FormListingType>(
    `${FORMS_LISTING_API_URL}?portal_id=${portalId}&skip=${skip}&limit=${limit}&form_type=normal`
  );

  return data;
};

export const useFormTable = (
  portalId: number,
  skip: number | null,
  limit: number | null
): UseQueryResult<FormListingType> => {
  return useQuery(
    [FORMS_LISTING_API_URL],
    () => formsListingTable(portalId, skip, limit),
    {
      refetchOnWindowFocus: false,
    }
  );
};

// Transaction - Section //

const transactionListingRequest = async (
  portalId: number,
  skip: number,
  limit: number
): Promise<TransactionListingType> => {
  const { data } = await axiosInstance.get<TransactionListingType>(
    `${TRANSACTION_LISTING_API_URL}?portal_id=${portalId}&skip=${skip}&limit=${limit}`
  );

  return data;
};

export const useTransactionListing = (
  portalId: number,
  skip: number,
  limit: number
): UseQueryResult<TransactionListingType> => {
  return useQuery(
    [TRANSACTION_LISTING_API_URL],
    () => transactionListingRequest(portalId, skip, limit),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const formDetailRequest = async (formId: string): Promise<FormDetailType> => {
  const { data } = await axiosInstance.get<FormDetailType>(
    `${GET_PORTAL_FORM_API_URL}${formId}`
  );

  return data;
};

export const useFormDetail = (
  formId: string
): UseQueryResult<FormDetailType> => {
  return useQuery(
    [GET_PORTAL_FORM_API_URL, formId],
    () => formDetailRequest(formId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const getHubspotFormsRequest = async (
  portalId: string
): Promise<HubspotFormsType> => {
  const { data } = await axiosInstance.get<HubspotFormsType>(
    `${GET_ALL_HUBSPOT_FORM_API_URL}`,
    {
      params: {
        portal_id: portalId,
      },
    }
  );

  return data;
};

export const useHubspotForms = (
  portalId: string
): UseQueryResult<HubspotFormsType> => {
  return useQuery(
    [GET_ALL_HUBSPOT_FORM_API_URL, portalId],
    () => getHubspotFormsRequest(portalId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const formsubmissionListingRequest = async (
  portalId: number,
  skip: number,
  left: number
): Promise<FormSubmissionListingType> => {
  const { data } = await axiosInstance.get<FormSubmissionListingType>(
    `${FORM_SUBMISSION_LISTING_API_URL}?portal_id=${portalId}&skip=${skip}&limit=${left}`
  );

  return data;
};

export const useFormSubmissionListing = (
  portalId: number,
  skip: number,
  left: number
): UseQueryResult<FormSubmissionListingType> => {
  return useQuery(
    [FORM_SUBMISSION_LISTING_API_URL],
    () => formsubmissionListingRequest(portalId, skip, left),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const billingDetailsRequest = async (
  portalId: number
): Promise<BillingDetailType> => {
  const { data } = await axiosInstance.get<BillingDetailType>(
    `${BILLING_DETAIL_API_URL}?portal_id=${portalId}`
  );

  return data;
};

export const useBillingDetails = (
  portalId: number
): UseQueryResult<BillingDetailType> => {
  return useQuery(
    [BILLING_DETAIL_API_URL],
    () => billingDetailsRequest(portalId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const razorpayPlansRequest = async (
  portalId: string
): Promise<RazorpayPlanResponse> => {
  const { data } = await axiosInstance.get<RazorpayPlanResponse>(
    `${RAZORPAY_PLANS_URL}?portal_id=${portalId}`
  );

  return data;
};

export const useRazorpayPlans = (
  portalId: string
): UseQueryResult<RazorpayPlanResponse> => {
  return useQuery(
    [RAZORPAY_PLANS_URL, portalId],
    () => razorpayPlansRequest(portalId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const createProductRequest = async (
  name: string,
  price: string,
  description: string,
  product_image: string,
  hs_product_id: string,
  portal_id: number
) => {
  const { data } = await axiosInstance.post(CREATE_PRODUCT_URL, {
    name: name,
    price: price,
    description: description,
    product_image: product_image,
    hs_product_id: hs_product_id,
    portal_id: portal_id,
  });

  return data;
};

export const useCreateProduct = (obj: {
  name: string;
  price: string;
  description: string;
  product_image: string;
  hs_product_id: string;
  portal_id: number;
}) => {
  return useQuery([CREATE_PRODUCT_URL, obj.portal_id], () =>
    createProductRequest(
      obj.name,
      obj.price,
      obj.description,
      obj.product_image,
      obj.hs_product_id,
      obj.portal_id
    )
  );
};
const hubspotProductIdRequest = async (
  portal_id: number
): Promise<HubspotProductList> => {
  const { data } = await axiosInstance.get<HubspotProductList>(
    `${EXISTING_HUBSPOT_PRODUCT_URL}?portal_id=${portal_id}`
  );

  return data;
};

export const useHubspotProducId = (obj: {
  portal_id: number;
}): UseQueryResult<HubspotProductList> => {
  return useQuery([EXISTING_HUBSPOT_PRODUCT_URL, obj.portal_id], () => {
    hubspotProductIdRequest(obj.portal_id);
  });
};

const paginationFunction = async (
  url: string,
  portal_id: number,
  skip: number,
  limit: number,
  email?: string,
  reference_id?: string
) => {
  if (url === 'fetchFormpayProduct') {
    const { data } = await axiosInstance.get<RootObjectProductListingType>(
      `${HOST_API}/formpayhome/product/fetchFormpayProduct?limit=${limit}&skip=${skip}&portal_id=${portal_id}&sort=true`
    );
    if (skip < 0) {
      return [];
    } else {
      return data;
    }
  }
  if (url === 'getPortalCoupon') {
    const { data } = await axiosInstance.get(
      `${HOST_API}/formpayhome/coupon/getPortalCoupon?limit=${limit}&skip=${skip}&portal_id=${portal_id}&sort=true`
    );
    if (skip < 0) {
      return [];
    } else {
      return data;
    }
  }
  if (url === 'getTransactions') {
    const { data } = await axiosInstance.get<TransactionListingType>(
      `${TRANSACTION_LISTING_API_URL}?portal_id=${portal_id}&skip=${skip}&limit=${limit}&reference_id=${reference_id}`
    );

    return data;
  }
  if (url === 'getPortalSubscription') {
    const { data } = await axiosInstance.get(
      `${SUBSCRIPTION_LISTING_API_URL}?portal_id=${portal_id}&skip=${skip}&limit=${limit}&email=${email}`
    );

    return data;
  }
  if (url === 'getFormSubmissions') {
    const { data } = await axiosInstance.post(
      `${FORM_SUBMISSION_LISTING_API_URL}?portal_id=${portal_id}&skip=${skip}&limit=${limit}`
    );

    return data;
  }
  if (url === 'getPortalNormalForms') {
    const { data } = await axiosInstance.post<FormListingType>(
      `${FORMS_LISTING_API_URL}?portal_id=${portal_id}&skip=${skip}&limit=${limit}&form_type=normal`
    );

    return data;
  }
  if (url === 'getPortalNormalQuotes') {
    const { data } = await axiosInstance.post<FormListingType>(
      `${FORMS_LISTING_API_URL}?portal_id=${portal_id}&skip=${skip}&limit=${limit}&form_type=quote`
    );

    return data;
  }
};

export const usePagination = (obj: {
  url: string;
  portal_id: number;
  skip: number;
  limit: number;
  email?: string;
  reference_id?: string;
}) => {
  return useQuery(
    [obj.url, obj.portal_id, obj.skip, obj.limit],
    () =>
      paginationFunction(
        obj.url,
        obj.portal_id,
        obj.skip,
        obj.limit,
        obj.email,
        obj.reference_id
      ),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const formPayProductFunction = async (portal_id: number) => {
  const { data } = await axiosInstance.get(
    `${HOST_API}/formpayhome/product/fetchFormpayProduct?portal_id=${portal_id}&sort=true`
  );

  return data;
};
const requestHubspotProduct = async (
  portalId: string
): Promise<HubspotProduct> => {
  const { data } = await axiosInstance.get<HubspotProduct>(
    `${FETCH_HUBSPOT_PRODUCT}?portal_id=${portalId}`
  );

  return data;
};

export const useFormPayProduct = (obj: { portal_id: number }) => {
  return useQuery(['fetchFormpayProducts', obj.portal_id], () =>
    formPayProductFunction(obj.portal_id)
  );
};

export const useHubspotProduct = (
  portalId: string
): UseQueryResult<HubspotProduct> => {
  return useQuery(
    [FETCH_HUBSPOT_PRODUCT, portalId],
    () => requestHubspotProduct(portalId),
    {
      refetchOnWindowFocus: false,
    }
  );
};

const allCouponFunction = async (portal_id: number) => {
  const { data } = await axiosInstance.get(
    `${HOST_API}/formpayhome/coupon/getPortalCoupon?portal_id=${portal_id}&sort=true`
  );

  return data;
};

export const useAllCoupons = (obj: { portal_id: number }) => {
  return useQuery(['getPortalCoupon', obj.portal_id], () =>
    allCouponFunction(obj.portal_id)
  );
};

const allFormCouponFunction = async (portal_id: number, formId: string) => {
  const { data } = await axiosInstance.get(
    `${HOST_API}/formpayhome/coupon/getPortalCoupon?portal_id=${portal_id}&formId=${formId}&sort=true`
  );

  return data;
};

export const useAllFormCoupons = (obj: {
  portal_id: number;
  formId: string;
}) => {
  return useQuery(
    ['getPortalCoupon', obj.portal_id, obj.formId],
    () => allFormCouponFunction(obj.portal_id, obj.formId),
    {
      refetchOnWindowFocus: false,
    }
  );
};
